import React, { Component } from 'react';
import { string, bool, arrayOf, array, func } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { isSameDay, isInclusivelyAfterDay, isInclusivelyBeforeDay } from 'react-dates';
import moment from 'moment';
import {
  required,
  bookingDatesRequired,
  composeValidators,
  bookingDatesMinimum,
  bookingConflictWithBookedDays,
} from '../../util/validators';
import { START_DATE, END_DATE, dateFromAPIToLocalNoon } from '../../util/dates';
import { propTypes, DELIVERY, PICKUP } from '../../util/types';
import * as validators from '../../util/validators';
import config from '../../config';
import {
  Form,
  IconSpinner,
  PrimaryButton,
  FieldDateRangeInput,
  FieldRadioGroup,
  FieldSelect,
  SecondaryButton,
  FieldTextInput,
} from '../../components';
import EstimatedBreakdownMaybe from './EstimatedBreakdownMaybe';
import arrayMutators from 'final-form-arrays';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { isDayNotAbleToBook } from '../../components/FieldDateRangeInput/DateRangeInput.helpers';
import css from './BookingDatesForm.css';
import { ensureListing } from '../../util/data';
import { types as sdkTypes } from '../../util/sdkLoader';
import debounce from 'lodash/debounce';
import { convertMoneyToNumber } from '../../util/currency';

const DEBOUNCE_WAIT_TIME = 500;

const identity = v => v;
const { Money } = sdkTypes;

export class BookingDatesFormComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      focusedInput: null,
      submitInProgress: false,
      submitError: null,
      seatInpDisabled: true,
      maxSeatShouldType: null,
      bookedDays: [],
    };
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.onFocusedInputChange = this.onFocusedInputChange.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.valuesFromLastSubmit = null;
    this.scrollRef = React.createRef();
    this.getBookedDays = this.getBookedDays.bind(this);
  }

  componentDidMount() {
    this.setState({
      bookedDays: [...this.getBookedDays()],
    });
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.timeSlots, this.props.timeSlots)) {
      this.setState({
        bookedDays: [...this.getBookedDays()],
      });
    }

    const justFinishedFetching =
      prevProps.fetchLineItemsInProgress && !this.props.fetchLineItemsInProgress;
    const hasNewLineItems =
      this.props.lineItems && !isEqual(this.props.lineItems, prevProps.lineItems);

    if (justFinishedFetching && hasNewLineItems && !this.props.fetchLineItemsError) {
      try {
        const { lineItems, listing } = this.props;
        const currentListing = ensureListing(listing);
        const { publicData = {} } = currentListing.attributes;

        const valueInCents = lineItems.reduce((total, item) => {
          if (item.includeFor && item.includeFor.includes('customer')) {
            return total + item.lineTotal.amount;
          }
          return total;
        }, 0);
        const value = valueInCents / 100;

        const { bookingDates } = this.valuesFromLastSubmit || {};
        const { startDate, endDate } = bookingDates || {};

        let quantity = 0;
        if (startDate && endDate) {
          const inclusiveEndDate = moment(endDate).subtract(1, 'days');
          quantity = inclusiveEndDate.diff(moment(startDate), 'days') + 1;
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            value: value,
            currency: 'AUD',
            items: [
              {
                item_id: currentListing.id.uuid,
                item_name: currentListing.attributes.title,
                item_brand: publicData.brand || null,
                item_category: publicData.category || null,
                item_variant: publicData.subCategory || null,
                quantity: quantity,
              },
            ],
          },
        });
      } catch (e) {
        console.error('GTM dataLayer push for add_to_cart failed:', e);
      }
    }
  }

  // Function that can be passed to nested components
  // so that they can notify this component when the
  // focused input changes.
  onFocusedInputChange(focusedInput) {
    this.setState({ focusedInput });
  }

  // In case start or end date for the booking is missing
  // focus on that input, otherwise continue with the
  // default handleSubmit function.
  handleFormSubmit(e) {
    const { startDate, endDate } = e.bookingDates || {};
    const { isNegotiate, payoutTotal } = this.props;
    e.isNegotiate = isNegotiate;
    e.payoutTotal = payoutTotal;
    if (!startDate) {
      e.preventDefault();
      this.setState({ focusedInput: START_DATE });
    } else if (!endDate) {
      e.preventDefault();
      this.setState({ focusedInput: END_DATE });
    } else {
      this.props.onSubmit(e);
    }
  }

  // When the values of the form are updated we need to fetch
  // lineItems from FTW backend for the EstimatedTransactionMaybe
  // In case you add more fields to the form, make sure you add
  // the values here to the bookingData object.
  handleOnChange(formValues) {
    const { isNegotiate, transactionId, isMobileLayout } = this.props;
    const { startDate, endDate } =
      formValues.values && formValues.values.bookingDates ? formValues.values.bookingDates : {};

    const { delivery, deliveryAddress, seats } = formValues.values || {};

    const listingId = this.props.listingId;
    const isOwnListing = this.props.isOwnListing;

    const hasDeliveryData = PICKUP === delivery || (DELIVERY === delivery && deliveryAddress);

    if ((!startDate || !endDate) && !this.state.seatInpDisabled) {
      this.setState({ seatInpDisabled: true });
    }

    const utcFormattedStartDate = moment(startDate).utc();
    const utcFormattedEndDate = moment(endDate).utc();
    utcFormattedStartDate.set('hour', 0);
    utcFormattedEndDate.set('hour', 0);

    const hasSeatData = parseInt(seats) > 0;

    if (startDate && endDate) {
      // Fetch timeSlot in this duration to get the minimum/maximum seat available
      const params = {
        listingId,
        start: utcFormattedStartDate.toDate(),
        end: utcFormattedEndDate.toDate(),
      };

      this.props.onTimeSlotsRequest(params).then(response => {
        const minSeat = response.reduce((acc, val) => {
          if (acc === null || acc > val.attributes.seats) {
            acc = val.attributes.seats;
            return acc;
          } else return acc;
        }, null);

        this.setState({ maxSeatShouldType: minSeat, seatInpDisabled: false });
      });
    }

    if (
      startDate &&
      endDate &&
      hasSeatData &&
      hasDeliveryData &&
      !this.props.fetchLineItemsInProgress
    ) {
      this.valuesFromLastSubmit = formValues.values;
      this.props
        .onFetchTransactionLineItems({
          bookingData: {
            startDate,
            endDate,
            seats,
            delivery,
            deliveryAddress,
            isNegotiate,
            transactionId,
          },
          listingId,
          isOwnListing,
        })
        .then(_ => {
          if (typeof window !== 'undefined' && isMobileLayout) {
            this.scrollRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        });
    }
  }

  getBookedDays = () => {
    const { timeSlots } = this.props;

    if (!timeSlots) {
      return [];
    }

    const result = [];

    const today = dateFromAPIToLocalNoon(
      moment()
        .utc()
        .startOf('day')
        .toDate()
    );
    const firstAvailableTimeslot = dateFromAPIToLocalNoon(timeSlots[0].attributes.start);
    const diffFromToday = moment(firstAvailableTimeslot).diff(moment(today), 'days');

    if (diffFromToday > 0) {
      for (let t = 0; t < diffFromToday; t++) {
        result.push(
          moment(today)
            .add(t, 'day')
            .toDate()
        );
      }
    }

    for (let i = 0; i < timeSlots.length - 1; i++) {
      const before = dateFromAPIToLocalNoon(timeSlots[i].attributes.start);
      const after = dateFromAPIToLocalNoon(timeSlots[i + 1].attributes.start);

      const diff = moment(after).diff(moment(before), 'days');

      if (diff === 1) {
        continue;
      }

      for (let t = 1; t < diff; t++) {
        result.push(
          moment(before)
            .add(t, 'day')
            .toDate()
        );
      }
    }

    return result;
  };

  render() {
    const { rootClassName, className, price: unitPrice, listing, ...rest } = this.props;
    const classes = classNames(rootClassName || css.root, className);
    const deliveryType = listing.attributes.publicData.deliveryType;
    const hasDelivery = Array.isArray(deliveryType) && deliveryType.includes(DELIVERY);

    const { minRent, pricingType } = get(listing, 'attributes.publicData', {});

    const debouncedOnChange = debounce(this.handleOnChange, DEBOUNCE_WAIT_TIME, {
      leading: false,
      trailing: true,
    });

    if (!unitPrice) {
      return (
        <div className={classes}>
          <p className={css.error}>
            <FormattedMessage id="BookingDatesForm.listingPriceMissing" />
          </p>
        </div>
      );
    }
    if (unitPrice.currency !== config.currency) {
      return (
        <div className={classes}>
          <p className={css.error}>
            <FormattedMessage id="BookingDatesForm.listingCurrencyInvalid" />
          </p>
        </div>
      );
    }

    return (
      <FinalForm
        {...rest}
        listing={listing}
        mutators={{
          ...arrayMutators,
        }}
        unitPrice={unitPrice}
        onSubmit={this.handleFormSubmit}
        initialValues={
          this.valuesFromLastSubmit || {
            delivery: !hasDelivery ? 'pickup' : 'delivery',
            bookingDates: this.props.bookingDates,
            seats: this.props.seats,
          }
        }
        render={fieldRenderProps => {
          const {
            endDatePlaceholder,
            startDatePlaceholder,
            formId,
            handleSubmit,
            intl,
            isOwnListing,
            submitButtonWrapperClassName,
            unitType,
            values,
            timeSlots,
            fetchTimeSlotsError,
            lineItems,
            fetchLineItemsInProgress,
            fetchLineItemsError,
            listing,
            valid,
            isNegotiate,
            onDeclineOffer,
            transitInProgress,
            transitError,
            isOffered,
            form,
            invalid,
            disabled,
          } = fieldRenderProps;
          const { startDate, endDate } = values && values.bookingDates ? values.bookingDates : {};

          const currentListing = ensureListing(listing);
          const { publicData = {} } = currentListing.attributes;

          const bookingStartLabel = intl.formatMessage({
            id: 'BookingDatesForm.bookingStartTitle',
          });
          const bookingEndLabel = intl.formatMessage({
            id: 'BookingDatesForm.bookingEndTitle',
          });
          const requiredMessage = intl.formatMessage({
            id: 'BookingDatesForm.requiredDate',
          });
          const startDateErrorMessage = intl.formatMessage({
            id: 'FieldDateRangeInput.invalidStartDate',
          });
          const endDateErrorMessage = intl.formatMessage({
            id: 'FieldDateRangeInput.invalidEndDate',
          });
          const bookingDatePeriodErrorMessage = intl.formatMessage(
            {
              id: 'FieldDateRangeInput.bookingDatePeriodError',
            },
            {
              minRent: `${publicData.minRent} ${publicData.pricingType}${
                publicData.minRent > 1 ? 's' : ''
              }`,
            }
          );
          const bookingConflictErrorMessage = intl.formatMessage({
            id: 'FieldDateRangeInput.bookingConflictErrorMessage',
          });

          const timeSlotsError = fetchTimeSlotsError ? (
            <p className={css.sideBarError}>
              <FormattedMessage id="BookingDatesForm.timeSlotsError" />
            </p>
          ) : null;

          const bondFeeBySeats = {
            amount: publicData.bondFee.amount * parseInt(values.seats),
            currency: publicData.bondFee.currency,
          };

          // This is the place to collect breakdown estimation data.
          // Note: lineItems are calculated and fetched from FTW backend
          // so we need to pass only booking data that is needed otherwise
          // If you have added new fields to the form that will affect to pricing,
          // you need to add the values to handleOnChange function
          const bookingData =
            startDate && endDate && valid
              ? {
                  unitType,
                  startDate,
                  endDate,
                  bondFee: bondFeeBySeats,
                  seats: values.seats,
                }
              : null;

          const showEstimatedBreakdown =
            bookingData && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;

          const bookingInfoMaybe = showEstimatedBreakdown ? (
            <div className={css.priceBreakdownContainer}>
              <h3 className={css.priceBreakdownTitle}>
                <FormattedMessage id="BookingDatesForm.priceBreakdownTitle" />
              </h3>
              <EstimatedBreakdownMaybe
                deliveryType={values.delivery}
                deliveryAddress={values.deliveryAddress}
                bookingData={bookingData}
                lineItems={lineItems}
              />
            </div>
          ) : null;

          const loadingSpinnerMaybe = fetchLineItemsInProgress ? (
            <IconSpinner className={css.spinner} />
          ) : null;

          const bookingInfoErrorMaybe = fetchLineItemsError ? (
            <span className={css.sideBarError}>
              <FormattedMessage id="BookingDatesForm.fetchLineItemsError" />
            </span>
          ) : null;

          const dateFormatOptions = {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          };

          const now = moment();
          const today = now.startOf('day').toDate();
          const tomorrow = now
            .startOf('day')
            .add(1, 'days')
            .toDate();

          const startDatePlaceholderText =
            startDatePlaceholder || intl.formatDate(today, dateFormatOptions);
          const endDatePlaceholderText =
            endDatePlaceholder || intl.formatDate(tomorrow, dateFormatOptions);
          const submitButtonClasses = classNames(
            submitButtonWrapperClassName || css.submitButtonWrapper
          );

          const seatLabel = intl.formatMessage({ id: 'BookingDatesForm.seat' });
          const seatRequired = validators.required(
            intl.formatMessage({
              id: 'BookingDatesForm.seatRequired',
            })
          );
          const minSeatRequired = validators.minValue(
            intl.formatMessage(
              {
                id: 'BookingDatesForm.minSeat',
              },
              { minSeat: config.listingMinimumSeats }
            ),
            config.listingMinimumSeats
          );
          const maxSeatRequired = validators.maxValue(
            intl.formatMessage(
              {
                id: 'BookingDatesForm.maxSeat',
              },
              { maxSeat: this.state.maxSeatShouldType }
            ),
            this.state.maxSeatShouldType
          );
          const seatValidators =
            this.state.maxSeatShouldType !== null
              ? validators.composeValidators(seatRequired, minSeatRequired, maxSeatRequired)
              : validators.composeValidators(seatRequired, minSeatRequired);
          const seatClasses = classNames(css.seat, {
            [css.seatInpDisabled]: this.state.seatInpDisabled,
          });

          const deliveryLabel = intl.formatMessage({ id: 'BookingDatesForm.deliveryLabel' });
          const requiredDeliveryLocation = intl.formatMessage({
            id: 'BookingDatesForm.requiredDeliveryLocation',
          });
          const deliveryLocationLabel = intl.formatMessage({
            id: 'BookingDatesForm.deliveryLocationLabel',
          });
          const deliveryRequired = intl.formatMessage({ id: 'BookingDatesForm.deliveryRequired' });

          const { deliveryLocations, deliveryFee: deliveryFeeObj } = publicData;

          const deliveryFee =
            deliveryFeeObj && deliveryFeeObj.amount !== undefined && deliveryFeeObj.currency
              ? new Money(deliveryFeeObj.amount, deliveryFeeObj.currency)
              : null;

          const hasDelivery = Array.isArray(deliveryType) && deliveryType.includes(DELIVERY);

          const isDelivery = values && values.delivery === DELIVERY;

          const showSelectDeliveryAddress =
            isDelivery &&
            hasDelivery &&
            Array.isArray(deliveryLocations) &&
            deliveryLocations.length > 0 &&
            deliveryFee;

          const errorMessage = transitError ? (
            <div className={css.errorMessage}>
              <FormattedMessage id="BookingDatesForm.errorMessage" />
            </div>
          ) : null;

          const { submitError } = this.state;
          const submitErrorMessage = submitError ? (
            <div className={css.errorMessage}>
              {submitError.message || <FormattedMessage id="BookingDatesForm.errorMessage" />}
            </div>
          ) : null;

          const submitInProgress = this.state.submitInProgress || transitInProgress;
          const submitDisabled = invalid || disabled || submitInProgress || !showEstimatedBreakdown;

          const endOfRange = config.dayCountAvailableForBooking - 1;

          const isOutsideRange = day =>
            startDate
              ? !isInclusivelyBeforeDay(
                  day,
                  moment(startDate)
                    .clone()
                    .add(endOfRange, 'days')
                ) || !isInclusivelyAfterDay(day, moment())
              : null;

          const isDayBlocked = startDate
            ? day => {
                return this.state.bookedDays.find(days => {
                  const localStartDate = dateFromAPIToLocalNoon(days);
                  return isSameDay(day, moment(localStartDate));
                });
              }
            : null;

          const isDayHighlighted = day => isDayNotAbleToBook(timeSlots, minRent, pricingType)(day);

          return (
            <Form onSubmit={handleSubmit} className={classes}>
              {timeSlotsError}
              <FormSpy
                subscription={{ values: true }}
                onChange={newData => {
                  const { startDate: newStartDate, endDate: newEndDate } =
                    (newData && newData.values && newData.values.bookingDates) || {};

                  if (
                    newData.values.bookingDates &&
                    newStartDate &&
                    newEndDate &&
                    startDate &&
                    endDate &&
                    (newStartDate.toString() !== startDate.toString() ||
                      newEndDate.toString() !== endDate.toString())
                  ) {
                    form.change('seats', 1);

                    if (newStartDate.toString() !== startDate.toString()) {
                      form.change('bookingDates.endDate', null);
                    }
                  }

                  debouncedOnChange(newData);
                }}
              />
              <FieldDateRangeInput
                className={css.bookingDates}
                name="bookingDates"
                unitType={unitType}
                startDateId={`${formId}.bookingStartDate`}
                startDateLabel={bookingStartLabel}
                startDatePlaceholderText={startDatePlaceholderText}
                endDateId={`${formId}.bookingEndDate`}
                endDateLabel={bookingEndLabel}
                endDatePlaceholderText={endDatePlaceholderText}
                focusedInput={this.state.focusedInput}
                onFocusedInputChange={this.onFocusedInputChange}
                format={identity}
                timeSlots={timeSlots}
                useMobileMargins
                validate={composeValidators(
                  required(requiredMessage),
                  bookingDatesRequired(startDateErrorMessage, endDateErrorMessage),
                  bookingDatesMinimum(bookingDatePeriodErrorMessage, minRent, pricingType),
                  bookingConflictWithBookedDays(bookingConflictErrorMessage, this.state.bookedDays)
                )}
                disabled={fetchLineItemsInProgress || isNegotiate}
                isOutsideRange={isOutsideRange}
                isDayHighlighted={isDayHighlighted}
                isDayBlocked={isDayBlocked}
              />

              <FieldTextInput
                id="seats"
                name="seats"
                className={seatClasses}
                type="number"
                label={seatLabel}
                validate={seatValidators}
                disabled={this.state.seatInpDisabled}
                step="1"
                min="1"
              />

              <FieldRadioGroup
                className={css.delivery}
                id={'delivery'}
                name={'delivery'}
                label={deliveryLabel}
                options={
                  hasDelivery
                    ? [
                        {
                          key: PICKUP,
                          label: intl.formatMessage({ id: 'BookingDatesForm.pickup' }),
                        },
                        {
                          key: DELIVERY,
                          label: intl.formatMessage({ id: 'BookingDatesForm.delivery' }),
                        },
                      ]
                    : [
                        {
                          key: PICKUP,
                          label: intl.formatMessage({ id: 'BookingDatesForm.pickup' }),
                        },
                      ]
                }
                validate={required(deliveryRequired)}
              />

              {showSelectDeliveryAddress ? (
                <FieldSelect
                  className={css.deliveryAddress}
                  name="deliveryAddress"
                  id="deliveryAddress"
                  label={deliveryLocationLabel}
                  validate={required(requiredDeliveryLocation)}
                >
                  <option value="" selected disabled>
                    {intl.formatMessage({ id: 'BookingDatesForm.pleaseSelectLocation' })}
                  </option>
                  {deliveryLocations.map((location, index) => (
                    <option key={index} value={JSON.stringify(location)}>
                      {location.address}
                    </option>
                  ))}
                </FieldSelect>
              ) : null}

              {bookingInfoMaybe}
              {loadingSpinnerMaybe}
              {bookingInfoErrorMaybe}

              <p ref={this.scrollRef} className={css.smallPrint}>
                <FormattedMessage
                  id={
                    isOwnListing
                      ? 'BookingDatesForm.ownListing'
                      : 'BookingDatesForm.youWontBeChargedInfo'
                  }
                />
              </p>
              <div className={submitButtonClasses}>
                <PrimaryButton
                  disabled={submitDisabled}
                  inProgress={submitInProgress}
                  type="submit"
                >
                  <FormattedMessage id="BookingDatesForm.requestToBook" />
                </PrimaryButton>
                {isOffered ? (
                  <SecondaryButton
                    onClick={onDeclineOffer}
                    disabled={submitDisabled}
                    inProgress={submitInProgress}
                    className={css.declineButton}
                    type="button"
                  >
                    <FormattedMessage id="BookingDatesForm.declineOffer" />
                  </SecondaryButton>
                ) : null}
              </div>
              {errorMessage}
              {submitErrorMessage}
            </Form>
          );
        }}
      />
    );
  }
}

BookingDatesFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  submitButtonWrapperClassName: null,
  price: null,
  isOwnListing: false,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  timeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

BookingDatesFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  submitButtonWrapperClassName: string,

  unitType: propTypes.bookingUnitType.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  timeSlots: arrayOf(propTypes.timeSlot),

  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  onTimeSlotsRequest: func.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,

  // for tests
  startDatePlaceholder: string,
  endDatePlaceholder: string,
};

const BookingDatesForm = compose(injectIntl)(BookingDatesFormComponent);
BookingDatesForm.displayName = 'BookingDatesForm';

export default BookingDatesForm;
